import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Pagination, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Alert, CircularProgress, Stack,
} from '@mui/material';
import { Add, CheckCircle } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const statusColors = { paid: 'success', pending: 'warning', overdue: 'error', cancelled: 'default' };

function CreateInvoiceDialog({ open, onClose, onCreated }) {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ tenant_id: '', plan_id: '', type: 'manual', billing_period: 'monthly', amount: '', discount: 0, tax: 0, notes: '', due_date: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/superadmin/tenants', { params: { limit: 200 } }).then(r => setTenants(r.data.data));
      api.get('/superadmin/plans').then(r => setPlans(r.data.data));
      setErr('');
    }
  }, [open]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await api.post('/superadmin/billing', form);
      onCreated();
      onClose();
      setForm({ tenant_id: '', plan_id: '', type: 'manual', billing_period: 'monthly', amount: '', discount: 0, tax: 0, notes: '', due_date: '' });
    } catch(e) { setErr(e.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const total = (+form.amount || 0) - (+form.discount || 0) + (+form.tax || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Invoice</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Tenant" value={form.tenant_id} onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value }))} required>
              {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name} ({t.email})</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Type" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {['subscription','renewal','manual','addon'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Billing Period" value={form.billing_period} onChange={e => setForm(f => ({ ...f, billing_period: e.target.value }))}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Plan (optional)" select value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
              <MenuItem value="">No plan</MenuItem>
              {plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Due Date" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Amount (Rs.)" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Discount" type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Tax" type="number" value={form.tax} onChange={e => setForm(f => ({ ...f, tax: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Notes" multiline rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={700}>Total: Rs.{fmt(total)}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.tenant_id || !form.amount}>{saving ? 'Creating...' : 'Create Invoice'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function MarkPaidDialog({ invoice, open, onClose, onPaid }) {
  const [form, setForm] = useState({ method: 'bank_transfer', reference_number: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/superadmin/billing/${invoice.id}/pay`, form);
      onPaid();
      onClose();
    } catch(e) { alert(e.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark Invoice Paid</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>Invoice: <strong>{invoice?.invoice_number}</strong> — Rs.{fmt(invoice?.total)}</Typography>
        <Stack spacing={2}>
          <TextField select size="small" label="Payment Method" fullWidth value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
            {['cash','bank_transfer','jazzcash','easypaisa','card','other'].map(m => <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m.replace('_',' ')}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Reference Number" fullWidth value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} />
          <TextField size="small" label="Notes" fullWidth multiline rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Mark Paid'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, sumRes] = await Promise.all([
        api.get('/superadmin/billing', { params: { page, limit: 20, status: statusFilter || undefined } }),
        api.get('/superadmin/billing/summary'),
      ]);
      setInvoices(invRes.data.data);
      setTotal(invRes.data.pagination?.total || 0);
      setSummary(sumRes.data.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Billing & Invoices</Typography>
          <Typography variant="body2" color="text.secondary">Manage all client invoices</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>New Invoice</Button>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total Collected', value: `Rs.${fmt(summary.summary?.total_collected)}`, color: 'success.main' },
            { label: 'MRR', value: `Rs.${fmt(summary.summary?.mrr)}`, color: 'primary.main' },
            { label: 'Pending', value: `Rs.${fmt(summary.summary?.pending)}`, color: 'warning.main' },
            { label: 'Overdue', value: `Rs.${fmt(summary.summary?.overdue)}`, color: 'error.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={6} md={3} key={label}>
              <Card><CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select size="small" label="Status" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} sx={{ width: 140 }}>
              <MenuItem value="">All</MenuItem>
              {['paid','pending','overdue','cancelled'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
            </TextField>
          </Box>

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{inv.invoice_number}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{inv.tenant_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{inv.tenant_email}</Typography>
                    </TableCell>
                    <TableCell>{inv.plan_name || '—'}</TableCell>
                    <TableCell><Chip label={inv.type} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right"><Typography fontWeight={600}>Rs.{fmt(inv.total)}</Typography></TableCell>
                    <TableCell><Chip label={inv.status} size="small" color={statusColors[inv.status] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{new Date(inv.created_at).toLocaleDateString('en-GB')}</Typography></TableCell>
                    <TableCell>
                      {inv.status === 'pending' && (
                        <Button size="small" startIcon={<CheckCircle />} color="success" onClick={() => setPayInvoice(inv)}>Pay</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>No invoices found</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 20)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <CreateInvoiceDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      {payInvoice && <MarkPaidDialog invoice={payInvoice} open={!!payInvoice} onClose={() => setPayInvoice(null)} onPaid={load} />}
    </Box>
  );
}
